/*
 * recents.ts — Phase 11 storage layer + pinned docs (V2 #2).
 */

import { openDB, type IDBPDatabase } from 'idb'

export interface Recent {
  id: number
  title: string
  source: string
  size: number
  savedAt: number
  pinned?: boolean
}

const DB_NAME = 'md-recents'
const STORE = 'recents'
const MAX = 5

let dbPromise: Promise<IDBPDatabase> | null = null

function openDbConn(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, 2, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const s = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
        s.createIndex('savedAt', 'savedAt')
      }
      if (oldVersion < 2 && db.objectStoreNames.contains(STORE)) {
        // pinned field is optional on each row — no index needed
      }
    },
    blocked() {
      dbPromise = null
    },
    terminated() {
      dbPromise = null
    },
  }).then((db) => {
    db.addEventListener('versionchange', () => {
      db.close()
      dbPromise = null
    })
    db.addEventListener('close', () => {
      dbPromise = null
    })
    return db
  })
}

async function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) dbPromise = openDbConn()
  return dbPromise
}

export async function listRecents(): Promise<Recent[]> {
  try {
    const db = await getDb()
    const all = (await db.getAllFromIndex(STORE, 'savedAt')) as Recent[]
    return all
      .slice()
      .sort((a, b) => {
        const ap = a.pinned ? 1 : 0
        const bp = b.pinned ? 1 : 0
        if (ap !== bp) return bp - ap
        if (b.savedAt !== a.savedAt) return b.savedAt - a.savedAt
        return b.id - a.id
      })
  } catch {
    return []
  }
}

export type SaveResult = { ok: true } | { ok: false; reason: string }

export async function saveRecent(r: Omit<Recent, 'id' | 'savedAt'>): Promise<SaveResult> {
  try {
    await doSave(r)
    return { ok: true }
  } catch (err) {
    const name = err instanceof DOMException ? err.name : ''
    if (name === 'QuotaExceededError' || /quota/i.test(String(err))) {
      try {
        await evictOldestUnpinned()
        await doSave(r)
        return { ok: true }
      } catch (err2) {
        return {
          ok: false,
          reason: err2 instanceof Error ? err2.message : String(err2),
        }
      }
    }
    return {
      ok: false,
      reason: err instanceof Error ? err.message : String(err),
    }
  }
}

async function doSave(r: Omit<Recent, 'id' | 'savedAt'>): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(STORE, 'readwrite')
  await tx.store.add({ ...r, savedAt: Date.now() })
  const all = (await tx.store.index('savedAt').getAll()) as Recent[]
  const unpinned = all
    .filter((x) => !x.pinned)
    .sort((a, b) => (b.savedAt !== a.savedAt ? b.savedAt - a.savedAt : b.id - a.id))
  for (const old of unpinned.slice(MAX)) {
    await tx.store.delete(old.id)
  }
  await tx.done
}

async function evictOldestUnpinned(): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(STORE, 'readwrite')
  const all = (await tx.store.index('savedAt').getAll()) as Recent[]
  const oldest = all.filter((x) => !x.pinned).sort((a, b) => a.savedAt - b.savedAt)[0]
  if (oldest) await tx.store.delete(oldest.id)
  await tx.done
}

export async function removeRecent(id: number): Promise<void> {
  try {
    const db = await getDb()
    await db.delete(STORE, id)
  } catch {
    /* non-critical */
  }
}

export async function setRecentPinned(id: number, pinned: boolean): Promise<void> {
  try {
    const db = await getDb()
    const row = (await db.get(STORE, id)) as Recent | undefined
    if (!row) return
    await db.put(STORE, { ...row, pinned })
  } catch {
    /* non-critical */
  }
}

export async function clearAllRecents(): Promise<void> {
  try {
    const db = await getDb()
    await db.clear(STORE)
  } catch {
    /* non-critical */
  }
}
